import { isNil } from 'lodash-es';

import type { AreaScaleLinear } from '../../types/guards.js';
import type {
  AnyXYChartScale,
  MapDatapoints,
  XYChartAxisDirection,
} from '../../types/xy-chart-internals.js';
import type { AreaDatapoint } from '../../types/xy-chart.js';
import { getBandCenteredPoint } from '../../utils/get-band-centered-point.js';
import { getCenterDate } from '../../utils/get-center-date.js';
import {
  isCategoricalAreaScale,
  isNumericalAreaScale,
  isTimeAreaScale,
} from '../../utils/type-guards.js';

export function applyNumericalBarScaleTransformations(
  args: AreaScaleLinear & { v1: number },
  accessorCoord: 'x' | 'y',
  baseline: XYChartAxisDirection,
  relAccessor?: ((value: number) => number) | undefined,
  accumulator?: number,
  binDomain?: number,
  isSeriesStacked?: boolean,
): AreaScaleLinear & { v1: number };

export function applyNumericalBarScaleTransformations(
  args: AreaScaleLinear,
  accessorCoord: 'x' | 'y',
  baseline: XYChartAxisDirection,
  relAccessor?: ((value: number) => number) | undefined,
  accumulator?: number,
  binDomain?: number,
  isSeriesStacked?: boolean,
): AreaScaleLinear;

export function applyNumericalBarScaleTransformations(
  args: AreaScaleLinear,
  accessorCoord: 'x' | 'y',
  baseline: XYChartAxisDirection,
  relAccessor?: ((value: number) => number) | undefined,
  accumulator?: number,
  binDomain?: number,
  isSeriesStacked?: boolean,
): AreaScaleLinear {
  const isNotBaseline = baseline !== undefined && accessorCoord !== baseline;
  let { v0, v1 } = args;

  if (isNotBaseline && accumulator !== undefined) {
    v0 = !isNil(v0) ? v0 + accumulator : accumulator;
    v1 = !isNil(v1) ? v1 + accumulator : accumulator;
  }

  const applyFn = (
    v: number | undefined,
    fn: (n: number) => number,
  ): number | undefined => (!isNil(v) ? fn(v) : v);

  if (relAccessor && isNotBaseline && !isNil(binDomain) && isSeriesStacked) {
    const effectiveDomain = binDomain || 1;
    return {
      v0: applyFn(v0, (n) => (n / effectiveDomain) * 100),
      v1: applyFn(v1, (n) => (n / effectiveDomain) * 100),
      scale: args.scale,
    };
  }

  if (relAccessor) {
    return {
      v0: applyFn(v0, relAccessor),
      v1: applyFn(v1, relAccessor),
      scale: args.scale,
    };
  }

  return { v0, v1, scale: args.scale };
}

/**
 * Creates an accessor function for the primary (non-stacking) axis.
 * For vertical areas, this is the X axis; for horizontal areas, this is the Y axis.
 *
 * - Numerical: returns scaled v0 value
 * - Time: returns scaled center of v0-v1 range
 * - Categorical: returns band-centered position
 */
const getPrimaryAxisAccessor = (accessorKeys: ['x0', 'x1'] | ['y0', 'y1']) => {
  return (scale: AnyXYChartScale, relAccessor?: (value: number) => number) => {
    return (datapoint: AreaDatapoint) => {
      const v0 = datapoint[accessorKeys[0]];
      const v1 = datapoint[accessorKeys[1]] as Date | undefined;

      if (v0 === undefined) {
        throw Error('unknown Accessor value for Area series');
      }

      const args = { scale, v0, v1 };

      if (isNumericalAreaScale(args)) {
        return args.scale(relAccessor ? relAccessor(args.v0) : args.v0);
      }

      if (isTimeAreaScale(args)) {
        const value = getCenterDate(args.v0, args.v1);
        return args.scale(value);
      }

      if (isCategoricalAreaScale(args)) {
        return getBandCenteredPoint(args.scale, args.v0);
      }

      throw Error('unknown Accessor value for Area series');
    };
  };
};

/**
 * Creates accessors for the stacking axis (the value axis).
 * For vertical areas, this is the Y axis (y0 to baseline, y1 to top).
 * For horizontal areas, this is the X axis (x0 to baseline, x1 to right edge).
 *
 * The baseline (v0Accessor) considers stacking accumulation.
 * The endpoint (v1Accessor) returns the cumulative value.
 *
 * @param accessorKeys - The keys to use for accessing datapoint values
 * @param baselineCoord - The coordinate that represents the baseline ('x' for vertical, 'y' for horizontal)
 */
const getGrowingAccessors = (
  accessorKeys: ['x0', 'x1'] | ['y0', 'y1'],
  baselineCoord: 'x' | 'y',
) => {
  const baselineAccessor = (
    scale: AnyXYChartScale,
    datapointsMetadata: MapDatapoints,
    relAccessor?: (value: number) => number,
    isSeriesStacked?: boolean,
  ) => {
    return (datapoint: AreaDatapoint) => {
      const v0 = datapoint[accessorKeys[0]];
      const v1 = datapoint[accessorKeys[1]];

      const { accumulator, baseline, binDomain } =
        datapointsMetadata.get(datapoint) ?? {};

      let args = { scale, v0, v1 };

      if (isNumericalAreaScale(args)) {
        args = applyNumericalBarScaleTransformations(
          args,
          baselineCoord === 'x' ? 'y' : 'x',
          baseline,
          relAccessor,
          accumulator,
          binDomain,
          isSeriesStacked,
        );
      }

      if (isNumericalAreaScale(args)) {
        const value = args.v0 ?? 0;
        return args.scale(value);
      }

      if (isTimeAreaScale(args)) {
        return args.scale(args.scale.invert(0));
      }

      if (isCategoricalAreaScale(args)) {
        return args.scale.range()[1];
      }

      throw Error('unknown baseline accessor for Area series');
    };
  };

  const endpointAccessor = (
    scale: AnyXYChartScale,
    datapointsMetadata: MapDatapoints,
    relAccessor?: (value: number) => number,
    isSeriesStacked?: boolean,
  ) => {
    return (datapoint: AreaDatapoint) => {
      const v0 = datapoint[accessorKeys[0]];
      const v1 = datapoint[accessorKeys[1]];
      const { accumulator, baseline, binDomain } =
        datapointsMetadata.get(datapoint) ?? {};

      if (v1 === undefined) {
        throw Error(
          `${accessorKeys[1]} cannot be undefined for the growing accessor in the AreaSeries`,
        );
      }

      let args = { scale, v0, v1 };

      if (isNumericalAreaScale(args)) {
        args = applyNumericalBarScaleTransformations(
          args,
          baselineCoord === 'x' ? 'y' : 'x',
          baseline,
          relAccessor,
          accumulator,
          binDomain,
          isSeriesStacked,
        );
      }

      if (isTimeAreaScale(args) || isNumericalAreaScale(args)) {
        return args.scale(args.v1);
      }

      if (isCategoricalAreaScale(args)) {
        const catWidth = Math.floor(args.scale.bandwidth() / 2);
        return (args.scale(args.v1) ?? 0) + catWidth;
      }

      throw Error('unknown value accessor for Area series');
    };
  };

  return { baselineAccessor, endpointAccessor };
};

// ============================================================================
// VERTICAL AREA ACCESSORS
// Primary axis: X (x0/x1), Growing/Stacking axis: Y (y0/y1)
// ============================================================================

/**
 * X accessor for the primary (category/time) axis in vertical area charts.
 * Used when x0/x1 define the categorical or time dimension.
 */
export const xAccessor = (
  scale: AnyXYChartScale,
  relAccessor?: (value: number) => number,
) => getPrimaryAxisAccessor(['x0', 'x1'])(scale, relAccessor);

/**
 * Vertical area stacking accessors (y0 = baseline, y1 = top)
 * baselineCoord is 'x' because vertical areas stack along the Y axis
 * while the X axis is the primary/categorical axis.
 */
export const { baselineAccessor: y0Accessor, endpointAccessor: y1Accessor } =
  getGrowingAccessors(['y0', 'y1'], 'x');

// ============================================================================
// HORIZONTAL AREA ACCESSORS
// Primary axis: Y (y0/y1), Growing/Stacking axis: X (x0/x1)
// ============================================================================

/**
 * Y accessor for the primary (category/time) axis in horizontal area charts.
 * Used when y0/y1 define the categorical or time dimension.
 */
export const yAccessor = (
  scale: AnyXYChartScale,
  relAccessor?: (value: number) => number,
) => getPrimaryAxisAccessor(['y0', 'y1'])(scale, relAccessor);

/**
 * Horizontal area stacking accessors (x0 = baseline, x1 = right edge)
 * baselineCoord is 'y' because horizontal areas stack along the X axis
 * while the Y axis is the primary/categorical axis.
 */
export const { baselineAccessor: x0Accessor, endpointAccessor: x1Accessor } =
  getGrowingAccessors(['x0', 'x1'], 'y');
